import { useMemo, useState } from 'react';

import { contactsApi, type HashedExportOptions } from '@/api/queries/contacts';
import { useModal } from '@/components/modal/useModal';
import { Button, Checkbox, Icon, IconButton, Input, RadioGroup, Textarea, useToast } from '@/components/ui';

import type { ConditionTree } from '@/api/queries/workspace';

type Algorithm = 'sha256' | 'hmac-sha256';
type ExportMode = 'plain' | 'hashed' | 'json';

const ALGORITHM_OPTIONS = [
  { value: 'sha256' as const, label: 'Plain SHA-256' },
  { value: 'hmac-sha256' as const, label: 'HMAC-SHA-256 with secret' },
];

const MODE_OPTIONS = [
  { value: 'plain' as const, label: 'Plain (decoded) — real field values' },
  { value: 'hashed' as const, label: 'Hashed identifiers — for cross-party matching' },
  { value: 'json' as const, label: 'JSON — full contact data' },
];

// Column ids must match ContactService.CsvHeaders on the backend.
const PLAIN_COLUMNS: { id: string; label: string }[] = [
  { id: 'id', label: 'ID' },
  { id: 'firstName', label: 'First name' },
  { id: 'lastName', label: 'Last name' },
  { id: 'primaryEmail', label: 'Primary email' },
  { id: 'phone', label: 'Phone' },
  { id: 'active', label: 'Active' },
  { id: 'doNotContact', label: 'Do not contact' },
  { id: 'tags', label: 'Tags' },
  { id: 'sources', label: 'Sources' },
  { id: 'createdAt', label: 'Created at' },
  { id: 'updatedAt', label: 'Updated at' },
  { id: 'version', label: 'Version' },
];

interface ExportHashedDialogProps {
  /** The active list filter, applied to the export so it matches what's on screen. */
  conditions: ConditionTree;
  /** Free-text quick search, applied alongside the conditions. */
  query?: string;
  /** Bulk selection, if any. When non-empty the export is restricted to these ids. */
  selectedIds: string[];
}

export function ExportHashedDialog({ conditions, query, selectedIds }: ExportHashedDialogProps) {
  const { closeModal } = useModal();
  const { show } = useToast();
  const exportHashed = contactsApi.useExportHashed();
  const exportPlain = contactsApi.useExportPlain();
  const exportJson = contactsApi.useExportJson();

  const [mode, setMode] = useState<ExportMode>('plain');
  const [algorithm, setAlgorithm] = useState<Algorithm>('sha256');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [usePrefixes, setUsePrefixes] = useState(true);
  const [includeIds, setIncludeIds] = useState(true);
  const [columns, setColumns] = useState<Set<string>>(
    () => new Set(PLAIN_COLUMNS.map((c) => c.id)),
  );

  const isHmac = algorithm === 'hmac-sha256';
  const secretMissing = isHmac && secret.trim().length === 0;
  const isExporting = exportHashed.isPending || exportPlain.isPending || exportJson.isPending;
  const canExport =
    mode === 'plain'
      ? columns.size > 0 && !isExporting
      : mode === 'json'
        ? !isExporting
        : !secretMissing && !isExporting;

  const toggleColumn = (id: string) =>
    setColumns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handlePlainExport = async () => {
    if (columns.size === 0) return;
    const scope = buildScope(conditions, query, selectedIds);
    try {
      await exportPlain.mutateAsync({
        // Preserve the canonical column order regardless of toggle order.
        columns: PLAIN_COLUMNS.filter((c) => columns.has(c.id)).map((c) => c.id),
        ...scope,
      });
      closeModal();
    } catch {
      show({ title: 'Export failed', severity: 'error' });
    }
  };

  const handleJsonExport = async () => {
    const scope = buildScope(conditions, query, selectedIds);
    try {
      await exportJson.mutateAsync(scope);
      closeModal();
    } catch {
      show({ title: 'Export failed', severity: 'error' });
    }
  };

  const summary = useMemo(
    () => buildSummary(algorithm, usePrefixes, includeIds),
    [algorithm, usePrefixes, includeIds],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      show({ title: 'Configuration copied', severity: 'success' });
    } catch {
      show({ title: 'Could not copy to clipboard', severity: 'error' });
    }
  };

  const handleExport = async () => {
    if (!canExport) return;
    const scope = buildScope(conditions, query, selectedIds);
    const opts: HashedExportOptions = {
      algorithm,
      hmacSecret: isHmac ? secret : undefined,
      usePrefixes,
      includeIds,
      ...scope,
    };
    try {
      await exportHashed.mutateAsync(opts);
      closeModal();
    } catch {
      // error surfaced via the banner below
    }
  };

  const scopeLabel =
    selectedIds.length > 0
      ? `${selectedIds.length} selected contact${selectedIds.length === 1 ? '' : 's'}`
      : 'all contacts matching the current filters';

  return (
    <>
      <div className="ds-modal-head">
        <h2>Export contacts</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={closeModal} />
      </div>

      <div className="ds-modal-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Section title="Export type">
            <RadioGroup
              name="export-mode"
              value={mode}
              options={MODE_OPTIONS}
              onChange={(v) => setMode(v)}
            />
          </Section>

          {mode === 'plain' && (
            <>
              <Banner>
                Exports <strong>{scopeLabel}</strong> as a CSV with the real (decoded) values
                for the columns you select below.
              </Banner>
              <Section title="Columns">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '6px 16px',
                  }}
                >
                  {PLAIN_COLUMNS.map((col) => (
                    <Checkbox
                      key={col.id}
                      checked={columns.has(col.id)}
                      onChange={() => toggleColumn(col.id)}
                      label={col.label}
                    />
                  ))}
                </div>
                {columns.size === 0 && (
                  <HelperText>Select at least one column to export.</HelperText>
                )}
              </Section>
            </>
          )}

          {mode === 'json' && (
            <Banner>
              Exports <strong>{scopeLabel}</strong> as a JSON file with the complete contact
              records — profile, contact info, professional data, consents, notes, comments
              and audit metadata. Change history, attachments and other media are not included.
            </Banner>
          )}

          {mode === 'hashed' && (
          <>
          <Banner>
            Exports <strong>{scopeLabel}</strong> as a CSV containing only{' '}
            <strong>{includeIds ? 'Id, Email, Phone' : 'Email, Phone'}</strong>,
            where Email is a hash of the normalized e-mail and Phone is a hash of the phone exactly as stored.
            Raw e-mails and phone numbers are never written to the file — the other party hashes their own data
            with the same rules and you compare hashes.
          </Banner>

          {/* A. Algorithm */}
          <Section title="Hashing algorithm">
            <RadioGroup
              name="export-hash-algorithm"
              value={algorithm}
              options={ALGORITHM_OPTIONS}
              onChange={(v) => setAlgorithm(v)}
            />

            {isHmac && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                <label htmlFor="export-hmac-secret" style={labelStyle}>
                  HMAC secret <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Input
                    id="export-hmac-secret"
                    type={showSecret ? 'text' : 'password'}
                    value={secret}
                    error={secretMissing}
                    autoComplete="off"
                    placeholder="Shared secret"
                    onChange={(e) => setSecret(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <IconButton
                    icon="eye"
                    label={showSecret ? 'Hide secret' : 'Show secret'}
                    onClick={() => setShowSecret((s) => !s)}
                  />
                </div>
                <HelperText>
                  The other party must use the <strong>same secret</strong> to produce matching hashes. The secret
                  is <strong>not</strong> included in the CSV and is never stored — share it through a secure channel,
                  separately from this file.
                </HelperText>
              </div>
            )}
          </Section>

          {/* B. Prefix option */}
          <Section title="Value type prefixes">
            <Checkbox
              checked={usePrefixes}
              onChange={setUsePrefixes}
              label="Use value type prefixes"
            />
            <HelperText>
              {usePrefixes ? (
                <>
                  Hash input is <code>email:{'{normalized_email}'}</code> and{' '}
                  <code>phone:{'{normalized_phone}'}</code>.
                </>
              ) : (
                <>Hash input is the bare normalized value, with no prefix.</>
              )}
            </HelperText>
            <Banner tone="warn">
              Both sides must use the <strong>same</strong> prefix setting — otherwise none of the hashes will match.
            </Banner>
          </Section>

          {/* B2. Identifier columns */}
          <Section title="Identifiers">
            <Checkbox
              checked={includeIds}
              onChange={setIncludeIds}
              label="Export IDs"
            />
            <HelperText>
              {includeIds ? (
                <>
                  The file includes a plain <code>Id</code> column alongside the hashed Email and Phone.
                </>
              ) : (
                <>The file contains only the hashed Email and Phone columns — no IDs.</>
              )}
            </HelperText>
          </Section>

          {/* C. Normalization explanation */}
          <Section title="How values are prepared">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={subheadStyle}>E-mail</div>
                <ul style={ulStyle}>
                  <li>Trim leading/trailing whitespace</li>
                  <li>Convert to lowercase</li>
                  <li>Encode as UTF-8 before hashing</li>
                </ul>
                <Example>{'" David.Cerny@Example.COM "  →  "david.cerny@example.com"'}</Example>
              </div>
              <div>
                <div style={subheadStyle}>Phone</div>
                <ul style={ulStyle}>
                  <li>Used exactly as stored — no normalization or formatting changes</li>
                </ul>
                <Example>{'"421910252047"  →  "421910252047"'}</Example>
              </div>
            </div>
          </Section>

          {/* D. Other-side config summary */}
          <Section title="Configuration for the other side">
            <HelperText>
              Send this to the other party so they normalize and hash their data identically. It does not contain
              the secret or any raw contact data.
            </HelperText>
            <Textarea value={summary} readOnly rows={16} style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon name="copy" size={13} />}
                onClick={handleCopy}
              >
                Copy configuration
              </Button>
            </div>
          </Section>

          {exportHashed.isError && (
            <Banner tone="error">Export failed. Please check your settings and try again.</Banner>
          )}
          </>
          )}
        </div>
      </div>

      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canExport}
          loading={isExporting}
          onClick={mode === 'plain' ? handlePlainExport : mode === 'json' ? handleJsonExport : handleExport}
        >
          {isExporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>
    </>
  );
}

/**
 * Maps the active list scope to the export request. A bulk selection narrows the
 * export to exactly those ids; otherwise the active condition tree is forwarded so
 * the export matches exactly what the list shows (compiled server-side by the same
 * ConditionTree engine the list's POST /contacts/_search uses).
 */
function buildScope(
  conditions: ConditionTree,
  query: string | undefined,
  selectedIds: string[],
): Pick<HashedExportOptions, 'selectedIds' | 'conditions' | 'query'> {
  if (selectedIds.length > 0) return { selectedIds };
  const hasConditions = (conditions.conditions?.length ?? 0) > 0;
  const trimmed = query?.trim() ? query.trim() : undefined;
  return { conditions: hasConditions ? conditions : undefined, query: trimmed };
}

function buildSummary(algorithm: Algorithm, usePrefixes: boolean, includeIds: boolean): string {
  const isHmac = algorithm === 'hmac-sha256';
  const lines = [
    'Hashed identifier export — matching configuration',
    '=================================================',
    `Algorithm: ${isHmac ? 'HMAC-SHA-256 (keyed)' : 'SHA-256'}`,
    `HMAC keyed: ${isHmac ? 'yes' : 'no'}`,
    `Value type prefixes: ${usePrefixes ? 'enabled (email:<value> / phone:<value>)' : 'disabled (bare value)'}`,
    '',
    'E-mail normalization:',
    '  1. Trim leading/trailing whitespace',
    '  2. Convert to lowercase',
    '  3. Encode as UTF-8 before hashing',
    '  Example: " David.Cerny@Example.COM " -> "david.cerny@example.com"',
    '',
    'Phone:',
    '  Used exactly as stored, with no normalization or formatting changes.',
    '  Example: "421910252047" -> "421910252047"',
    '',
    'Hash output format: lowercase hexadecimal',
    `CSV columns: ${includeIds ? 'Id, Email, Phone' : 'Email, Phone'}`,
    'Note: raw e-mails and phone numbers are NOT included in the file.',
  ];
  if (isHmac) {
    lines.push('', 'The HMAC secret must be exchanged separately through a secure channel.');
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------- small presentational helpers

const labelStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)' };
const subheadStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, marginBottom: 4 };
const ulStyle: React.CSSProperties = { margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h3 style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>{title}</h3>
      {children}
    </section>
  );
}

function HelperText({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--text-3)' }}>{children}</p>;
}

function Example({ children }: { children: React.ReactNode }) {
  return (
    <pre
      style={{
        margin: '6px 0 0',
        padding: '8px 10px',
        background: 'var(--surface-2, var(--bg-2))',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'monospace',
        fontSize: 11.5,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        color: 'var(--text-2)',
      }}
    >
      {children}
    </pre>
  );
}

function Banner({ children, tone = 'info' }: { children: React.ReactNode; tone?: 'info' | 'warn' | 'error' }) {
  const palette =
    tone === 'error'
      ? { bg: 'var(--red-soft)', fg: 'var(--red)', icon: 'alert' as const }
      : tone === 'warn'
        ? { bg: 'var(--amber-soft, var(--brand-soft))', fg: 'var(--amber-ink, var(--brand-ink))', icon: 'alert' as const }
        : { bg: 'var(--brand-soft)', fg: 'var(--brand-ink)', icon: 'info' as const };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        background: palette.bg,
        color: palette.fg,
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Icon name={palette.icon} size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
